import { useState, useEffect, useRef } from 'react';
import { useLang } from '../contexts/LanguageContext';

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function NotreVision() {
  const { t } = useLang();
  const [heroRef, heroVis] = useReveal(0.1);
  const [s1Ref, s1Vis] = useReveal(0.15);
  const [s1TxtRef, s1TxtVis] = useReveal(0.2);
  const [s2Ref, s2Vis] = useReveal(0.15);
  const [s2TxtRef, s2TxtVis] = useReveal(0.2);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative aspect-video md:aspect-auto md:h-[80vh] flex items-center justify-center overflow-hidden bg-black">
        <img src="/photos/IMG_4116.jpeg" alt="La Fabrication"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.85) contrast(1) saturate(0.95)', objectPosition: '50% 90%' }} />
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.15)' }} />

        <div ref={heroRef} className={`relative z-10 text-center px-6 transition-all duration-[1.5s] ${heroVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-3 sm:gap-6 mb-4 sm:mb-10">
            <div className="w-8 sm:w-16 h-px bg-white/30" />
            <img src="/photos/Logo_fd_transparent.png" alt="" className="h-6 sm:h-12 w-auto"
              style={{ filter: 'invert(1) brightness(2)', opacity: 0.5 }} />
            <div className="w-8 sm:w-16 h-px bg-white/30" />
          </div>
          <h1 className="text-white font-light" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(22px, 5vw, 68px)'
          }}>
            {t('fabrication.heroTitle')}
          </h1>
          <p className="text-white/50 mt-2 sm:mt-4" style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 'clamp(10px, 1.1vw, 17px)'
          }}>
            {t('fabrication.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* ── LA FABRICATION — Section 1 ── */}
      <section className="relative py-20 md:py-28 bg-white overflow-hidden">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 items-center gap-10 md:gap-0">
          <div ref={s1Ref}
            className={`flex justify-center transition-all duration-[1.5s] ${s1Vis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <img src="/photos/IMG_0962.jpeg" alt="Fabrication artisanale"
              className="w-[80%] max-w-[320px] md:max-w-none md:w-[400px] lg:w-[450px] h-auto object-cover shadow-lg" />
          </div>

          <div ref={s1TxtRef}
            className={`flex items-center px-8 md:px-10 lg:px-16 transition-all duration-[1.5s] delay-300 ${s1TxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="max-w-md" style={{ fontFamily: "'Inter', sans-serif" }}>
              <h2 className="mb-8 font-normal leading-snug" style={{ color: '#2B2B2B', fontSize: 'clamp(20px, 1.8vw, 24px)' }}>
                {t('fabrication.s1Title')}
              </h2>

              <div className="space-y-6">
                <p className="leading-[1.9] text-[15px]" style={{ color: '#7a7a7a' }}>
                  {t('fabrication.s1P1')}
                </p>
                <p className="leading-[1.9] text-[15px]" style={{ color: '#7a7a7a' }}>
                  {t('fabrication.s1P2')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 2 — En toute transparence ── */}
      <section className="relative py-20 md:py-28 bg-black overflow-hidden">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 items-center gap-10 md:gap-8">
          <div ref={s2TxtRef}
            className={`flex justify-center px-6 md:px-10 lg:px-16 order-2 md:order-1 transition-all duration-[1.5s] delay-300 ${s2TxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="w-full max-w-xl" style={{ fontFamily: "'Inter', sans-serif" }}>
              <h2 className="mb-10 text-white font-light" style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(26px, 2.4vw, 34px)'
              }}>
                {t('fabrication.s2Title')}
              </h2>

              <p className="text-white text-[16px] leading-[1.6] mb-8">
                {t('fabrication.s2Intro')}
              </p>

              <div className="space-y-5 text-white/55 leading-[1.8] text-[13.5px]">
                <p>{t('fabrication.s2P1')}</p>
                <p>{t('fabrication.s2P2')}</p>
                <p>{t('fabrication.s2P3')}</p>
                <p>{t('fabrication.s2P4')}</p>
              </div>
            </div>
          </div>

          <div ref={s2Ref}
            className={`flex justify-center order-1 md:order-2 transition-all duration-[1.5s] ${s2Vis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <img src="/photos/IMG_0972.jpeg" alt="Pierres sélectionnées"
              className="w-[85%] max-w-[360px] md:max-w-none md:w-[430px] lg:w-[480px] h-auto object-contain" />
          </div>
        </div>
      </section>

    </div>
  );
}
